<script setup>

import { ref, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthActions } from '@auth';
import { i18n, PopUpManager as PopUp, STATE, verifyField } from '@/Ship';
import { useProfileActions } from '@profile';

const signIn = useAuthActions('signIn');
const getProfileResource = useProfileActions('getProfileResource');

const router = useRouter();
const profile = getProfileResource();
const rememberMe = ref(false);
const showPassword = ref(false);

const payload = reactive({
    email: '',
    password: '',
});

if (localStorage.getItem('rememberMe')) {
    payload.email = localStorage.getItem('email');
    payload.password = localStorage.getItem('password');
    rememberMe.value = true;
}

const errors = reactive({
    email: null,
    password: null,
});

const email = computed({
    get: () => payload.email,
    set: (value) => {
        payload.email = value;
        errors.email = verifyField(email.value, ['require', 'email']);
        return true;
    }
});

const password = computed({
    get: () => payload.password,
    set: (value) => {
        payload.password = value;
        return true;
    }
});

const login = () => {
    if (!errors.email) {
        signIn(payload.email, payload.password).then(() => {

            if (rememberMe.value) {
                localStorage.setItem('email', payload.email);
                localStorage.setItem('password', payload.password);
                localStorage.setItem('rememberMe', true);
            } else {
                localStorage.removeItem('email');
                localStorage.removeItem('password');
                localStorage.removeItem('rememberMe');
            }

            return profile.load().then(() => {
                STATE.IS_LOGIN = true;
                router.push({ name: 'Trading', params: router.currentRoute.value.params });
            });
        }).catch(() => {
            PopUp.open(require('@auth/components/signUpInfoPopUp.vue'))
            .setCaption(i18n.global.t('base.info'))
            .setPayload({
                payload:
                    {
                        mainText: i18n.global.t('base.something_wrong'),
                        additionText: i18n.global.t('auth.wrong_login_or_password')
                    }
            });
        });
    }
};
</script>

<template>
    <form class="sign-in" v-on:submit.prevent="login">
        <g-caption size="2" weight="600">
            {{ $t('auth.sign_in') }}

            <router-link v-bind:to="{ name: 'SignUp', params: { locale: $i18n.locale } }" tag="a" class="link sign-up">
                {{ $t('auth.sign_up', 'Sign Up') }}
            </router-link>
        </g-caption>

        <label class="field">
            <span>{{ $t('auth.email') }}</span>
            <g-input v-model="email"
                     type="email"
                     v-bind:error="errors.email"/>
        </label>

        <label class="field">
            <span>{{ $t('auth.password') }}</span>
            <g-input v-model="password"
                     v-bind:type="showPassword ? 'text' : 'password'"
                     v-bind:error="errors.password">

                <g-symbol v-bind:name="showPassword ? 'eye-open' : 'eye-close'"
                          width="20"
                          height="20"
                          v-on:click="showPassword = !showPassword"/>

            </g-input>
        </label>

        <router-link v-bind:to="{ name: 'ResetPassword', params: { locale: $i18n.locale } }" tag="a" class="link">
            {{ $t('auth.forgot_password') }}
        </router-link>

        <g-flex align="center" justify="between">
            <g-button>
                {{ $t('auth.sign_in') }}
            </g-button>

            <g-checkbox v-bind:label="$t('auth.remember')" v-model:checked="rememberMe">{{ $t('auth.remember', 'Remember Me') }}</g-checkbox>
        </g-flex>

        <!--
        <g-flex align="end" justify="start" gap="16">
            {{ $t('auth.have_account', 'Do not have an account') }}
            <router-link v-bind:to="{ name: 'SignUp', params: { locale: $i18n.locale } }" tag="a" class="link">
                {{ $t('auth.sign_up', 'Sign Up') }}
            </router-link>
        </g-flex>
        -->
    </form>
</template>

<style lang="scss" scoped>
.sign-in {
    width: 100%;
    max-width: 360px;
    padding: 20px 18px 30px;
}

.g-caption {
    margin-bottom: 25px;
    display: flex;
    justify-content: space-between;
    color: var(--title-text-color, $title-text-color);

    & > .sign-up {
        text-align: center;
        width: 120px;
        font-size: 70%;
        transition: font-size 0.3s;

        &:hover {
            font-size: 100%;
        }
    }
}

.link {
    font-size: 106%;
    font-weight: 600;
    color: var(--success, $success);
    transition: color 0.2s;

    &:hover {
        color: var(--success-hover, $success-hover);
    }
}

.g-button {
    margin: 25px 0;
    padding: 0 20px;
    min-width: 100px;
    background-color: var(--login-btn, $login-btn);

    &:hover {
        background-color: var(--login-btn-hover, $login-btn-hover);
    }
}

.field {
    flex-basis: 100%;
    display: block;
    margin-bottom: 6px;

    & > span {
        font-size: 1.1rem;
        font-weight: 600;
        color: var(--title-tetx-color, $title-text-color) !important;
    }
}
</style>